/**
 * 达人订单详情页 - 达人打卡服务时间线
 * 参考工作台 PunchTimelineSection 实现，包含四个打卡节点
 */

import type { StaffOrderDetail } from "@/api/staff";

interface PunchCardProps {
  data: StaffOrderDetail;
}

interface PunchRecord {
  type: string;
  status: "done" | "pending";
  time?: string;
  addr?: string;
  content?: string;
  img?: string;
  color: string;
}

/** 解析可能的JSON数组字符串 */
function parseImgs(imgs: string | string[] | null | undefined): string[] {
  if (!imgs) return [];
  if (Array.isArray(imgs)) return imgs;
  try {
    return JSON.parse(imgs);
  } catch {
    return [];
  }
}

export default function PunchCard({ data: d }: PunchCardProps) {
  const depart = d.is_depart ?? 0;
  const st = d.status ?? 0;

  // 解析打卡图片
  const signImgs = parseImgs(d.sign_imgs);
  const staffImgs = parseImgs(d.staff_imgs as unknown as string);

  // 构建打卡记录列表
  const records: PunchRecord[] = [];

  // 1. 达人出发打卡
  if (depart >= 1) {
    records.push({
      type: "出发打卡",
      status: "done",
      time: d.depart_time_desc || "",
      addr: d.depart_addr || "",
      content: "已确认出发，前往客户指定地点",
      color: "#7C4DFF",
    });
  } else {
    records.push({
      type: "出发打卡",
      status: "pending",
      color: "#7C4DFF",
    });
  }

  // 2. 达人到达打卡
  if (depart >= 2) {
    records.push({
      type: "到达打卡",
      status: "done",
      time: d.arrive_time_desc || "",
      addr: d.arrive_addr || "",
      content: "已到达该地点，等待客户",
      color: "#2979FF",
    });
  } else {
    records.push({
      type: "到达打卡",
      status: "pending",
      color: "#2979FF",
    });
  }

  // 3. 服务开始打卡（优先 start_time_desc，兜底 sign_time_desc）
  const serviceStarted = depart >= 3 && st >= 30;
  if (serviceStarted) {
    records.push({
      type: "服务开始",
      status: "done",
      time: d.start_time_desc || d.sign_time_desc || "",
      addr: d.start_addr || d.sign_address || "",
      content: d.sign_content || "已确认身份，服务正式开始",
      img: signImgs[0],
      color: "#00BFA5",
    });
  } else {
    records.push({
      type: "服务开始",
      status: "pending",
      color: "#00BFA5",
    });
  }

  // 4. 服务完成打卡
  const isFinishDone = st >= 40;
  records.push({
    type: "服务完成",
    status: isFinishDone ? "done" : "pending",
    time: d.finish_time_desc || "",
    addr: d.finish_addr || "",
    content: typeof d.staff_remark === "string" && d.staff_remark ? d.staff_remark : "服务已完成",
    img: isFinishDone ? staffImgs[0] : undefined,
    color: "#FF8A65",
  });

  const doneCount = records.filter((r) => r.status === "done").length;
  const totalCount = records.length;

  return (
    <div className="mx-4 mt-3 bg-white rounded-2xl p-4">
      {/* 标题行 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <i
            className="ri-checkbox-multiple-line"
            style={{ fontSize: 16, color: "#6366F1" }}
          />
          <span className="text-sm font-bold text-gray-900">达人打卡服务</span>
        </div>
        <span className="text-xs font-medium" style={{ color: "#6366F1" }}>
          {`${doneCount}/${totalCount} 已完成`}
        </span>
      </div>

      {/* 提示说明 */}
      <div
        className="flex items-start gap-2 px-3 py-2.5 rounded-xl mb-4"
        style={{ background: "#F8FAFC" }}
      >
        <i
          className="ri-information-line flex-shrink-0 mt-0.5"
          style={{ fontSize: 14, color: "#94A3B8" }}
        />
        <span className="text-xs leading-relaxed" style={{ color: "#64748B" }}>
          以下打卡记录由达人在服务过程中实时上传。包含位置、图像及备签信息，均可查看。
        </span>
        <i
          className="ri-arrow-up-s-line flex-shrink-0 ml-auto"
          style={{ fontSize: 16, color: "#CBD5E1" }}
        />
      </div>

      {/* 打卡时间线 */}
      <div className="pl-1">
        {records.map((item, idx) => (
          <PunchItem key={item.type} item={item} idx={idx} totalDone={doneCount} />
        ))}
      </div>
    </div>
  );
}

/** 单个打卡项 */
function PunchItem({
  item,
  idx,
  totalDone,
}: {
  item: PunchRecord;
  idx: number;
  totalDone: number;
}) {
  const isLast = idx === 3; // 最后一个节点

  return (
    <div className="flex gap-3 relative">
      {/* 左侧时间线 */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center z-[1]"
          style={{
            background: item.status === "done" ? item.color : "#F1F5F9",
            border: item.status === "done" ? "none" : "2px solid #E2E8F0",
          }}
        >
          {item.status === "done" ? (
            <i className="ri-check-line" style={{ fontSize: 12, color: "#fff" }} />
          ) : (
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#CBD5E1" }} />
          )}
        </div>
        {!isLast && (
          <div
            className="w-0.5 flex-1 mt-1"
            style={{
              background: idx < totalDone ? `${item.color}40` : "#E2E8F0",
              minHeight: 24,
            }}
          />
        )}
      </div>

      {/* 右侧内容 */}
      <div className="flex-1 pb-5">
        {/* 标题 + 状态标签 */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-semibold text-gray-900">{item.type}</span>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-medium"
            style={{
              background: item.status === "done" ? "#EEF2FF" : "#F1F5F9",
              color: item.status === "done" ? "#6366F1" : "#94A3B8",
            }}
          >
            {item.status === "done" ? "已打卡" : "待打卡"}
          </span>
        </div>

        {/* 已打卡：详细展示 */}
        {item.status === "done" ? (
          <div className="space-y-2">
            {item.time && (
              <div className="flex items-center gap-1.5">
                <i className="ri-time-line flex-shrink-0" style={{ fontSize: 13, color: "#9CA3AF" }} />
                <span className="text-xs" style={{ color: "#6B7280" }}>{item.time}</span>
              </div>
            )}
            {item.addr && (
              <div className="flex items-start gap-1.5">
                <i className="ri-map-pin-line flex-shrink-0 mt-0.5" style={{ fontSize: 13, color: "#9CA3AF" }} />
                <span className="text-xs leading-relaxed" style={{ color: "#6B7280" }}>{item.addr}</span>
              </div>
            )}
            {item.content && (
              <div className="flex items-start gap-1.5">
                <i className="ri-file-text-line flex-shrink-0 mt-0.5" style={{ fontSize: 13, color: "#9CA3AF" }} />
                <span className="text-xs leading-relaxed" style={{ color: "#6B7280" }}>{item.content}</span>
              </div>
            )}
            {item.img && (
              <img
                src={item.img}
                alt=""
                className="w-20 h-20 rounded-lg object-cover mt-1"
              />
            )}
          </div>
        ) : (
          /* 待打卡：根据类型显示不同提示 */
          <p className="text-xs leading-relaxed" style={{ color: "#9CA3AF" }}>
            {item.type === "出发打卡"
              ? "等待达人确认出发"
              : item.type === "到达打卡"
                ? "等待达人到达集合点"
                : item.type === "服务开始"
                  ? "等待达人开始服务"
                  : "等待服务完成"}
          </p>
        )}
      </div>
    </div>
  );
}
